import {
  autoUpdate,
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  flip,
  offset,
  shift,
  type UseFloatingOptions,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import { css, cx } from '@linaria/core'
import type React from 'react'
import type { FC } from 'react'
import { viewWidths } from '#store/layoutConfiguration'
import { useSharedAppSelector } from '#store/sharedBaseStore'
import { style } from '#styles/index'

const modalClass = css`
  display: flex;
  flex-direction: column;

  align-items: center;
  align-content: center;
  justify-content: center;
  width: calc(100vw * var(--inverse-zoom));
  height: calc(100vh * var(--inverse-zoom));
  
  &[data-placement='top'] {
    justify-content: start;
  }

  &[data-placement='bottom'] {
    justify-content: end; 
  }
 
  &._blurred {
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(5px);
  }

  > ._dialog {
    background-color: ${style.theme.backgroundSurfacePrimaryStrong};
    overflow: hidden;
    width: fit-content;
    
    max-width: calc(100vw - 48px);
    max-height: calc(80vh);
    
    height: fit-content;
    display: flex;
    flex-direction: column;


    &[data-wideness='${viewWidths.slim}'] {
      max-width: calc(min(100vw, 1024px) - 48px);
    }

    &[data-wideness='${viewWidths.regular}'] {
      max-width: calc(min(100vw, 1600px) - 48px);
    }

    &[data-wideness='${viewWidths.wide}'] {
      max-width: calc(min(100vw, 1920px) - 48px);
    }

    &[data-wideness='${viewWidths.ultraWide}'] {
      max-width: calc(min(100vw, 2560px) - 48px);
    }

    &.full-size {
      height: 95vh;
      
      &[data-wideness='${viewWidths.slim}'] {
        width: calc(min(100vw, 1024px) - 48px);
      }

      &[data-wideness='${viewWidths.regular}'] {
        width: calc(min(100vw, 1600px) - 48px);
      }

      &[data-wideness='${viewWidths.wide}'] {
        width: calc(min(100vw, 1920px) - 48px);
      }

      &[data-wideness='${viewWidths.ultraWide}'] {
        width: calc(min(100vw, 2560px) - 48px);
      }
    }
    
    &._shadow {
      box-shadow: 0 4px 8px 0px rgba(0, 0, 0, 0.24);
    }
  }

  body[data-theme='dark'] &._box-blur {
    background: rgba(255, 255, 255, 0.3);
  }

  &._lifted {
    background: rgba(0, 0, 0, 0.6);
  }
`

interface ModalOptions {
  placement?: 'top' | 'center' | 'bottom'
  shadow?: boolean
  // blurred?: boolean
  lifted?: boolean
  open?: boolean
  className?: string
  lockScroll?: boolean
  contentClassName?: string
  onOpenChange?: UseFloatingOptions['onOpenChange']
  dismissOnOutside?: boolean
  fullSize?: boolean
  'data-testid'?: string
}

export const Modal: FC<ModalOptions & { children: React.ReactNode }> = ({
  shadow,
  // blurred,
  lifted,
  children,
  open,
  className,
  placement,
  lockScroll,
  contentClassName,
  onOpenChange,
  dismissOnOutside,
  fullSize,
  'data-testid': dataTestId,
}) => {
  const layoutConfiguration = useSharedAppSelector((x) => x.layoutConfiguration)

  const { refs, context } = useFloating({
    open,
    middleware: [offset(100), flip(), shift()],
    whileElementsMounted: autoUpdate,
    onOpenChange: onOpenChange,
  })

  const dismiss = useDismiss(context, { outsidePressEvent: 'click', enabled: dismissOnOutside !== false })

  const role = useRole(context, {
    role: 'alertdialog',
  })
  const { getFloatingProps } = useInteractions([dismiss, role])
  if (!open) {
    return undefined
  }

  return (
    <FloatingPortal>
      <FloatingOverlay className={cx(className, modalClass, lifted && '_lifted')} lockScroll={lockScroll && false} data-placement={placement}>
        <FloatingFocusManager context={context}>
          <div
            ref={refs.setFloating}
            // style={floatingStyles}
            {...getFloatingProps()}
            className={cx('_dialog', shadow && '_shadow', fullSize && 'full-size', contentClassName)}
            data-wideness={layoutConfiguration.width.width}
            data-testid={dataTestId}
          >
            {children}
          </div>
        </FloatingFocusManager>
      </FloatingOverlay>
    </FloatingPortal>
  )
}
